import type { PostgrestError } from '@supabase/supabase-js'

/**
 * Handle PostgREST errors gracefully for .schema('cogo') queries.
 * Returns true if the error is a known infrastructure issue that should
 * be treated as "no data" rather than a hard error.
 *
 * PGRST106: Schema not exposed in PostgREST config
 * PGRST205: Table does not exist in the schema
 *
 * @see 397-UX-FINDINGS.md FIND-002 for root cause analysis
 */
export function isSchemaInfraError(error: PostgrestError | null): boolean {
  if (!error) return false
  return error.code === 'PGRST106' || error.code === 'PGRST205'
}

/**
 * Wrap a Supabase .schema('cogo') query result with graceful fallback.
 * If the error is a schema infrastructure issue, returns the fallback value
 * instead of propagating the error.
 */
export function handleCogoSchemaResult<T>(
  result: { data: T | null; error: PostgrestError | null },
  fallback: T,
): { data: T; error: string | null } {
  if (result.error) {
    if (isSchemaInfraError(result.error)) {
      console.warn(`[cogo-schema] ${result.error.code}: ${result.error.message} — returning fallback`)
      return { data: fallback, error: null }
    }
    return { data: fallback, error: result.error.message }
  }
  return { data: result.data ?? fallback, error: null }
}
